import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { sqlRead, turnoverWrite as sqlWrite } from "../utils/sqlClient";
import { fmtDate } from "./turnover/shared";
import { useIsMobile } from "../hooks/useIsMobile";
import { syncHubSpotData, hubspotProxy } from "../utils/hubspotSync";
import { useStaleData } from "../hooks/useStaleData";
import { loginScopes } from "../auth/msalConfig";
import { getMailToken } from "../utils/msalHelpers";
import { buildJobNumberAssignedEmail, jobNumberAssignedRecipients, marketingDirector } from "./turnover/jobNumberAssignedEmail";
import { hubspotReadinessChecks } from "./turnover/hubspotReadiness";
import { ASSIGN_PARAM, parseAssignParam, resolveAssignTarget } from "./turnover/assignLink";
import { subfolderNames, layoutOf, sectionNumberOf } from "../config/turnoverLayout";
import { ENTITY_BASE_PATH, listJobFolders } from "../utils/sharePoint";
import { jobFolderName, jobFolderForNumber } from "./turnover/folderPath";
import { copyDocsToJobFolder, docsInEstimatingSite, copySummary } from "./turnover/moveDocsToJobFolder";

// ── Constants ──────────────────────────────────────────────────────────────────

// Digits-only value in state (matches what sp_CreateTurnover expects) with a
// comma/currency-formatted display in the input — strip on every keystroke,
// format only for display.
function digitsOnly(v) {
  return (v || "").replace(/[^\d]/g, "");
}
function formatThousands(v) {
  const digits = digitsOnly(v);
  return digits ? Number(digits).toLocaleString("en-US") : "";
}
function formatCurrency(v) {
  const digits = digitsOnly(v);
  return digits ? "$" + Number(digits).toLocaleString("en-US") : "";
}

const STATUS_CONFIG = {
  "Contract Review":  { bg: "#f0fdf4", color: "#166534", dot: "#22c55e" },
  "Pending Number":   { bg: "#fef3c7", color: "#92400e", dot: "#f59e0b" },
  "Numbered":         { bg: "#dbeafe", color: "#1e40af", dot: "#3b82f6" },
  "Active":           { bg: "#ede9fe", color: "#5b21b6", dot: "#8b5cf6" },
  "In Progress":      { bg: "#e0f2fe", color: "#0369a1", dot: "#0ea5e9" },
  "Package Complete": { bg: "#d1fae5", color: "#065f46", dot: "#10b981" },
  "Turned Over":      { bg: "#f3f4f6", color: "#374151", dot: "#9ca3af" },
};

// Deliberate variant of turnover/shared.js fmtCurrency: returns "—" (not null)
// on missing values — this page renders the result directly in table cells.
function fmtCurrency(v) {
  if (!v) return "—";
  const n = parseFloat(v);
  if (isNaN(n)) return v;
  return "$" + n.toLocaleString("en-US", { maximumFractionDigits: 0 });
}

function fmtM(n) {
  // Compact format: $1.2M, $754K, $13.1M
  if (n >= 1000000) return "$" + (n / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
  if (n >= 1000)    return "$" + Math.round(n / 1000) + "K";
  return "$" + Math.round(n).toLocaleString();
}

function parseLineItems(json) {
  if (!json) return null;
  try {
    const items = typeof json === "string" ? JSON.parse(json) : json;
    const total    = items.reduce((s, li) => s + (parseFloat(li.amount) || 0), 0);
    const released = items.filter(li => li.awarded === "true" || li.awarded === true)
                          .reduce((s, li) => s + (parseFloat(li.amount) || 0), 0);
    return { total, released, forecasted: total - released, count: items.length };
  } catch { return null; }
}

// ── Status pill ────────────────────────────────────────────────────────────────
function StatusPill({ status }) {
  const cfg = STATUS_CONFIG[status] || { bg: "#f3f4f6", color: "#374151", dot: "#9ca3af" };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      fontSize: 11, fontWeight: 600, padding: "3px 9px",
      borderRadius: 100, background: cfg.bg, color: cfg.color,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.dot, flexShrink: 0 }} />
      {status}
    </span>
  );
}

// ── Progress bar ───────────────────────────────────────────────────────────────
function ProgressBar({ pct, complete, total }) {
  const color = pct === 100 ? "#10b981" : pct > 50 ? "#3b82f6" : "#f59e0b";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ flex: 1, height: 5, background: "#e5e7eb", borderRadius: 99, overflow: "hidden" }}>
        <div style={{ width: `${pct || 0}%`, height: "100%", background: color, borderRadius: 99, transition: "width 0.3s" }} />
      </div>
      <span style={{ fontSize: 11, color: "var(--text3)", whiteSpace: "nowrap", minWidth: 42 }}>
        {complete}/{total}
      </span>
    </div>
  );
}

// ── New Turnover Modal ─────────────────────────────────────────────────────────
function NewTurnoverModal({ onClose, onCreated, currentUser }) {
  const [hubspotId, setHubspotId] = useState("");
  const [hubspotLoading, setHubspotLoading] = useState(false);
  const [hubspotError, setHubspotError] = useState("");
  const [hsReadiness, setHsReadiness] = useState(null); // null = not yet checked
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    project_name: "",
    hubspot_deal_id: "",
    client_name: "",
    owner_address: "", // display-only — not persisted, pulled from the HubSpot "Owner" company
    salesperson: "",
    salesperson_email: "",
    project_manager: "",
    city_state: "",
    market: "",
    project_type: "",
    contract_structure: "",
    labor_strategy: "",
    arch_materials: "",
    square_feet: "",
    deal_amount: "",
    planned_turnover_date: "",
    deal_description: "",
    estimator: "",
    estimator_email: "",
  });

  function set(field, val) {
    setForm(f => ({ ...f, [field]: val }));
  }

  // Fetches all HubSpot owners (active + archived/deactivated) once and builds
  // an id -> { name, email } map. More robust than resolving owner IDs one at
  // a time, since deactivated users 404 on individual /owners/{id} lookups but
  // still show up in the archived owners list.
  async function fetchOwnerMap() {
    const map = {};
    for (const archived of [false, true]) {
      try {
        let after;
        do {
          const url = `https://api.hubapi.com/crm/v3/owners/?limit=100&archived=${archived}${after ? `&after=${after}` : ""}`;
          const res = await hubspotProxy(url);
          (res.results || []).forEach(o => {
            map[String(o.id)] = {
              name: [o.firstName, o.lastName].filter(Boolean).join(" ") || o.email || String(o.id),
              email: o.email || "",
            };
          });
          after = res.paging?.next?.after;
          if (after) await new Promise(r => setTimeout(r, 300));
        } while (after);
      } catch {}
    }
    return map;
  }

  async function lookupHubSpot() {
    if (!hubspotId.trim()) return;
    setHubspotLoading(true);
    setHubspotError("");
    setHsReadiness(null);
    try {
      const dealId = hubspotId.trim();
      const PROPS = "dealname,amount,closedate,hubspot_owner_id,city_state,market,room_type,owner_direct_,labor_strategy,architectural_materials,square_feet,description,lead_source,lead_source_detail,bd2,estimators";

      // Fetch deal properties
      const deal = await hubspotProxy(
        `https://api.hubapi.com/crm/v3/objects/deals/${dealId}?properties=${PROPS}`
      );
      const p = deal.properties || {};
      await new Promise(r => setTimeout(r, 600));

      // Build a full owner map (active + archived) once, instead of resolving
      // each owner ID with its own request. This also resolves estimators who
      // are deactivated HubSpot users, which one-off /owners/{id} lookups miss.
      const ownerMap = await fetchOwnerMap();
      await new Promise(r => setTimeout(r, 300));

      const primaryOwner = p.hubspot_owner_id ? ownerMap[String(p.hubspot_owner_id)] : null;
      const ownerName  = primaryOwner?.name  || "";
      const ownerEmail = primaryOwner?.email || "";

      // Resolve the estimator owner ID to name + email. Each deal has exactly
      // one estimator and one salesperson — "estimators" is a HubSpot
      // user-reference property but is only ever populated with a single ID.
      let estimatorName = "";
      let estimatorEmail = "";
      if (p.estimators) {
        const estimatorId = String(p.estimators).split(/[,;]\s*/).filter(Boolean)[0];
        const estimatorOwner = estimatorId ? ownerMap[estimatorId] : null;
        estimatorName  = estimatorOwner?.name  || estimatorId || "";
        estimatorEmail = estimatorOwner?.email || "";
      }

      setForm(f => ({
        ...f,
        hubspot_deal_id:    dealId,
        project_name:       p.dealname       || f.project_name,
        client_name:        f.client_name,
        salesperson:        ownerName        || f.salesperson,
        salesperson_email:  ownerEmail       || f.salesperson_email,
        city_state:         p.city_state     || f.city_state,
        market:             p.market         || f.market,
        project_type:       p.room_type      || f.project_type,
        contract_structure: p.owner_direct_ === "true" ? "Direct to Owner" : p.owner_direct_ || f.contract_structure,
        labor_strategy:     p.labor_strategy || f.labor_strategy,
        arch_materials:     p.architectural_materials || f.arch_materials,
        square_feet:        p.square_feet    ? String(p.square_feet) : f.square_feet,
        deal_amount:        p.amount         ? String(p.amount)      : f.deal_amount,
        lead_source:        p.lead_source    || f.lead_source,
        lead_source_detail: p.lead_source_detail || f.lead_source_detail,
        sales_rep2:         p.bd2            || f.sales_rep2,
        estimator:          estimatorName    || f.estimator,
        estimator_email:    estimatorEmail   || f.estimator_email,
        deal_description:   p.description    || f.deal_description,
      }));

      // ── HubSpot readiness validation ─────────────────────────────────────
      // Fetch associations in parallel after the deal properties are in state.
      await new Promise(r => setTimeout(r, 300));
      const [companiesRes, contactsRes, lineItemsRes] = await Promise.allSettled([
        hubspotProxy(`https://api.hubapi.com/crm/v3/objects/deals/${dealId}/associations/companies`),
        hubspotProxy(`https://api.hubapi.com/crm/v3/objects/deals/${dealId}/associations/contacts`),
        hubspotProxy(`https://api.hubapi.com/crm/v3/objects/deals/${dealId}/associations/line_items`),
      ]);

      // Fetch association labels for companies and contacts
      const companyIds = (companiesRes.value?.results || []).map(r => String(r.id || r.toObjectId));
      const contactIds = (contactsRes.value?.results || []).map(r => String(r.id || r.toObjectId));
      const lineItemIds = (lineItemsRes.value?.results || []).map(r => String(r.id || r.toObjectId));

      // Batch-read company and contact labels via v4 associations endpoint.
      // Each result's associationTypes[] always includes a HUBSPOT_DEFINED
      // default entry with label:null alongside any USER_DEFINED custom
      // label (e.g. "Billing Contact", "Owner") — only the latter has a
      // usable string label, so null-labeled entries are filtered out before
      // lowercasing (previously fell back to the numeric typeId, which has
      // no .toLowerCase() and threw — silently swallowed by the catch below,
      // which made every deal fail these two checks regardless of actual
      // HubSpot labels; session note added 2026-07-09).
      // Narrow match for auto-filling the "Owner" field specifically — a deal
      // can have BOTH an Owner (the client/property owner, e.g. a pharmacy
      // company) and a separate Contract Holder / General Contractor (e.g.
      // DPR Construction) as two different companies. Only the literal
      // "Owner" label should populate the Owner field; conflating it with
      // Contract Holder/GC pulled in the wrong company when both existed.
      const OWNER_EXACT_LABELS = ["owner"];

      let companyLabels = [], contactLabels = [], companyAssocResults = [];
      if (companyIds.length) {
        try {
          const res = await hubspotProxy(
            `https://api.hubapi.com/crm/v4/objects/deals/${dealId}/associations/companies`
          );
          companyAssocResults = res.results || [];
          companyLabels = companyAssocResults.flatMap(r =>
            (r.associationTypes || r.associationLabels || [])
              .map(l => l.label)
              .filter(Boolean)
              .map(l => l.toLowerCase())
          );
        } catch (e) {
          console.error("HubSpot readiness: company label fetch failed", e);
        }
      }

      // Pull the Owner company's name + address so the "Owner" field on this
      // form is auto-filled instead of hand-typed.
      let ownerCompanyName = "", ownerCompanyAddress = "", ownerCompanyCity = "", ownerCompanyState = "";
      const ownerAssoc = companyAssocResults.find(r =>
        (r.associationTypes || r.associationLabels || []).some(l =>
          l.label && OWNER_EXACT_LABELS.some(o => l.label.toLowerCase() === o)
        )
      );
      const ownerCompanyId = ownerAssoc ? String(ownerAssoc.toObjectId || ownerAssoc.id) : null;
      if (ownerCompanyId) {
        try {
          const res = await hubspotProxy(
            `https://api.hubapi.com/crm/v3/objects/companies/batch/read`, "POST",
            { properties: ["name", "address", "city", "state", "zip"], inputs: [{ id: ownerCompanyId }] }
          );
          const props = res.results?.[0]?.properties || {};
          ownerCompanyName  = props.name  || "";
          ownerCompanyCity  = props.city  || "";
          ownerCompanyState = props.state || "";
          ownerCompanyAddress = [props.address, [props.city, props.state, props.zip].filter(Boolean).join(", ")]
            .filter(Boolean).join(", ");
        } catch (e) {
          console.error("HubSpot: owner company detail fetch failed", e);
        }
      }
      if (ownerCompanyName || ownerCompanyAddress) {
        setForm(f => ({
          ...f,
          client_name:   ownerCompanyName    || f.client_name,
          owner_address: ownerCompanyAddress || f.owner_address,
          // Only fill City/State from the owner company if the deal itself
          // didn't already supply one (p.city_state, set earlier above).
          city_state: f.city_state || (ownerCompanyCity || ownerCompanyState
            ? [ownerCompanyCity, ownerCompanyState].filter(Boolean).join(", ")
            : f.city_state),
        }));
      }
      if (contactIds.length) {
        try {
          const res = await hubspotProxy(
            `https://api.hubapi.com/crm/v4/objects/deals/${dealId}/associations/contacts`
          );
          contactLabels = (res.results || []).flatMap(r =>
            (r.associationTypes || r.associationLabels || [])
              .map(l => l.label)
              .filter(Boolean)
              .map(l => l.toLowerCase())
          );
        } catch (e) {
          console.error("HubSpot readiness: contact label fetch failed", e);
        }
      }

      // Owner and Contract Holder are two separate required labels
      // (turnover/hubspotReadiness.js) — the same company may carry both.
      const checks = hubspotReadinessChecks({
        companyLabels, contactLabels,
        companyCount: companyIds.length, lineItemCount: lineItemIds.length,
      });

      setHsReadiness({ checks, dealId });
    } catch (e) {
      setHubspotError(e.message);
    } finally {
      setHubspotLoading(false);
    }
  }

  async function handleCreate() {
    if (!form.project_name.trim()) return;
    setSaving(true);
    try {
      const payload = JSON.stringify({
        ...form,
        created_by:       currentUser?.name  || "",
        created_by_email: currentUser?.email || "",
      }).replace(/'/g, "''");
      const result = await sqlWrite(`EXEC sp_CreateTurnover @payload = '${payload}'`);

      // sp_CreateTurnover's final SELECT returns the new TurnoverID — same
      // multi-shape fallback sqlRead() uses elsewhere, since the passthrough
      // Logic App can wrap results a couple different ways.
      const rows = result?.ResultSets?.Table1 || result?.Table1 || result?.value || [];
      const newTurnoverId = rows?.[0]?.TurnoverID || null;

      // Pre-populate companies/contacts/line items from HubSpot right away so
      // the Cover Sheet is already filled in the first time anyone opens this
      // turnover — no manual "Refresh HubSpot" click required. Best-effort:
      // a failure here shouldn't block turnover creation, since Refresh
      // HubSpot on the detail page is always available as a fallback.
      if (newTurnoverId && form.hubspot_deal_id) {
        try {
          await syncHubSpotData(form.hubspot_deal_id, newTurnoverId, currentUser);
        } catch (syncErr) {
          console.warn("Initial HubSpot sync failed (non-blocking):", syncErr.message);
        }
      }

      onCreated();
    } catch (e) {
      alert("Error creating turnover: " + e.message);
    } finally {
      setSaving(false);
    }
  }

  const inputStyle = {
    width: "100%", padding: "7px 10px", fontSize: 13,
    border: "1px solid var(--cs-border)", borderRadius: 6,
    background: "var(--surface)", color: "var(--text)",
    boxSizing: "border-box",
  };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: "var(--text2)", marginBottom: 4, display: "block" };
  const rowStyle = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
      padding: 16, boxSizing: "border-box",
    }}>
      <div style={{
        background: "var(--surface)", borderRadius: 12,
        width: "100%", maxWidth: 640, maxHeight: "90vh", display: "flex", flexDirection: "column",
        boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
      }}>
        {/* Header */}
        <div style={{
          padding: "20px 24px 16px", borderBottom: "1px solid var(--cs-border)",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text)" }}>New Turnover Package</div>
            <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 2 }}>
              Pull from HubSpot or enter manually
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "var(--text3)", lineHeight: 1 }}>×</button>
        </div>

        {/* HubSpot lookup bar */}
        <div style={{ padding: "14px 24px", background: "#f8f9fb", borderBottom: "1px solid var(--cs-border)" }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text2)", marginBottom: 6 }}>
            🔗 Import from HubSpot Deal
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              value={hubspotId}
              onChange={e => setHubspotId(e.target.value)}
              onKeyDown={e => e.key === "Enter" && lookupHubSpot()}
              placeholder="HubSpot Deal ID (e.g. 12345678)"
              style={{ ...inputStyle, flex: 1 }}
            />
            <button
              onClick={lookupHubSpot}
              disabled={hubspotLoading || !hubspotId.trim()}
              style={{
                padding: "7px 16px", fontSize: 13, fontWeight: 600,
                background: "var(--cs-blue)", color: "#fff",
                border: "none", borderRadius: 6, cursor: "pointer",
                opacity: (hubspotLoading || !hubspotId.trim()) ? 0.5 : 1,
                whiteSpace: "nowrap",
              }}
            >
              {hubspotLoading ? "Loading…" : "Import"}
            </button>
          </div>
          {hsReadiness && (() => {
            const { checks } = hsReadiness;
            const passed  = checks.filter(c => c.pass).length;
            const total   = checks.length;
            const allPass = passed === total;
            const pct     = Math.round((passed / total) * 100);
            const hardFails = checks.filter(c => !c.pass);
            return (
              <div style={{
                marginTop: 10, border: "1px solid",
                borderColor: allPass ? "#10b981" : "#ef4444",
                borderRadius: 8, overflow: "hidden",
              }}>
                {/* Header */}
                <div style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "7px 12px",
                  background: allPass ? "#d1fae5" : "#fee2e2",
                }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: allPass ? "#065f46" : "#991b1b" }}>
                    🎯 HubSpot Readiness — Deal {hsReadiness.dealId}
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: allPass ? "#065f46" : "#991b1b" }}>
                    {pct}% ({passed}/{total})
                  </span>
                </div>
                {/* Progress bar */}
                <div style={{ height: 3, background: "#e5e7eb" }}>
                  <div style={{
                    height: "100%", width: `${pct}%`,
                    background: allPass ? "#10b981" : "#ef4444",
                  }} />
                </div>
                {/* Checks */}
                <div style={{ padding: "8px 12px", display: "flex", flexDirection: "column", gap: 4, background: "#fff" }}>
                  {checks.map(c => (
                    <div key={c.key} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12 }}>
                      <span style={{ flexShrink: 0 }}>{c.pass ? "✅" : "🔴"}</span>
                      <div>
                        <span style={{ fontWeight: 600, color: c.pass ? "#065f46" : "#991b1b" }}>{c.label}</span>
                        {!c.pass && <div style={{ fontSize: 11, color: "#6b7280", marginTop: 1 }}>{c.failMsg}</div>}
                      </div>
                    </div>
                  ))}
                </div>
                {!allPass && (
                  <div style={{
                    padding: "6px 12px", background: "#fff5f5",
                    borderTop: "1px solid #fca5a5", fontSize: 11, color: "#991b1b",
                  }}>
                    Fix {hardFails.length} issue{hardFails.length > 1 ? "s" : ""} in HubSpot, then re-import to enable Create Turnover
                  </div>
                )}
              </div>
            );
          })()}
          {hubspotError && (
            <div style={{ marginTop: 6, fontSize: 11, color: "var(--red)" }}>{hubspotError}</div>
          )}
        </div>

        {/* Form body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

            <div>
              <label style={labelStyle}>Project Name *</label>
              <input value={form.project_name} onChange={e => set("project_name", e.target.value)} style={inputStyle} placeholder="e.g. Lonza VV Filler Replacement" />
            </div>

            <div className="mobile-stack-grid" style={rowStyle}>
              <div>
                <label style={labelStyle}>Owner</label>
                <input value={form.client_name} onChange={e => set("client_name", e.target.value)} style={inputStyle} placeholder="Pulled from the HubSpot company tagged Owner" />
                {form.owner_address && (
                  <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4 }}>{form.owner_address}</div>
                )}
              </div>
              <div>
                <label style={labelStyle}>City / State</label>
                <input value={form.city_state} onChange={e => set("city_state", e.target.value)} style={inputStyle} placeholder="e.g. Boston, MA" />
              </div>
            </div>

            <div className="mobile-stack-grid" style={rowStyle}>
              <div>
                <label style={labelStyle}>Salesperson</label>
                <input value={form.salesperson} onChange={e => set("salesperson", e.target.value)} style={inputStyle} placeholder="Name" />
              </div>
              <div>
                <label style={labelStyle}>Salesperson Email</label>
                <input value={form.salesperson_email} onChange={e => set("salesperson_email", e.target.value)} style={inputStyle} placeholder="email@cleanspaceus.com" />
              </div>
            </div>

            <div>
              <label style={labelStyle}>Planned Turnover Date</label>
              <input type="date" value={form.planned_turnover_date} onChange={e => set("planned_turnover_date", e.target.value)} style={inputStyle} />
            </div>

            <div className="mobile-stack-grid" style={rowStyle}>
              <div>
                <label style={labelStyle}>Market</label>
                <input value={form.market} onChange={e => set("market", e.target.value)} style={inputStyle} placeholder="e.g. Pharmaceutical, BioTech" />
              </div>
              <div>
                <label style={labelStyle}>Project Type / CleanSpace Role</label>
                <input value={form.project_type} onChange={e => set("project_type", e.target.value)} style={inputStyle} placeholder="e.g. Turn-Key, Envelope" />
              </div>
            </div>

            <div className="mobile-stack-grid" style={rowStyle}>
              <div>
                <label style={labelStyle}>Contract Structure</label>
                <input value={form.contract_structure} onChange={e => set("contract_structure", e.target.value)} style={inputStyle} placeholder="e.g. Direct to Owner" />
              </div>
              <div>
                <label style={labelStyle}>Labor Strategy</label>
                <input value={form.labor_strategy} onChange={e => set("labor_strategy", e.target.value)} style={inputStyle} placeholder="e.g. CleanSpace Non-Union" />
              </div>
            </div>

            <div className="mobile-stack-grid" style={rowStyle}>
              <div>
                <label style={labelStyle}>Architectural Materials</label>
                <input value={form.arch_materials} onChange={e => set("arch_materials", e.target.value)} style={inputStyle} placeholder="e.g. CleanSpace Modular" />
              </div>
              <div>
                <label style={labelStyle}>Square Feet</label>
                <input value={formatThousands(form.square_feet)} onChange={e => set("square_feet", digitsOnly(e.target.value))} style={inputStyle} placeholder="e.g. 4,500" inputMode="numeric" />
              </div>
            </div>

            <div>
              <label style={labelStyle}>Deal Amount</label>
              <input value={formatCurrency(form.deal_amount)} onChange={e => set("deal_amount", digitsOnly(e.target.value))} style={inputStyle} placeholder="e.g. $1,250,000" inputMode="numeric" />
            </div>

          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: "16px 24px", borderTop: "1px solid var(--cs-border)",
          display: "flex", justifyContent: "flex-end", gap: 10,
        }}>
          <button onClick={onClose} style={{
            padding: "8px 18px", fontSize: 13, fontWeight: 600,
            background: "none", border: "1px solid var(--cs-border)",
            borderRadius: 6, cursor: "pointer", color: "var(--text2)",
          }}>
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={saving || !form.project_name.trim() || (hsReadiness && hsReadiness.checks.some(c => !c.pass))}
            title={hsReadiness && hsReadiness.checks.some(c => !c.pass) ? "Fix HubSpot issues above before creating" : ""}
            style={{
              padding: "8px 20px", fontSize: 13, fontWeight: 600,
              background: "var(--cs-blue)", color: "#fff",
              border: "none", borderRadius: 6, cursor: "pointer",
              opacity: (saving || !form.project_name.trim() || (hsReadiness && hsReadiness.checks.some(c => !c.pass))) ? 0.5 : 1,
            }}
          >
            {saving ? "Creating…" : "Create Turnover"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Job Number Modal (Accounting) ──────────────────────────────────────────────
// ── Graph helpers for folder provisioning ─────────────────────────────────────
const CONST_SP_SITE   = "alliedconstructionmanagemen.sharepoint.com:/sites/construction";
const TEMPLATE_NAME   = "Project Folder Template";
const GRAPH_BASE      = "https://graph.microsoft.com/v1.0";

// Construction SharePoint base folder per contracting entity. TECHspace has no
// dedicated folder tree yet, so it falls back to CleanSpace's per current convention.

async function getConstructionDriveInfo(token) {
  const siteRes = await fetch(`${GRAPH_BASE}/sites/${CONST_SP_SITE}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!siteRes.ok) throw new Error(`Site lookup failed (${siteRes.status})`);
  const site    = await siteRes.json();
  const drvRes  = await fetch(`${GRAPH_BASE}/sites/${site.id}/drives`, { headers: { Authorization: `Bearer ${token}` } });
  if (!drvRes.ok) throw new Error(`Drive lookup failed (${drvRes.status})`);
  const drives  = await drvRes.json();
  const drive   = drives.value.find(d => d.name === "Documents" || d.name === "Shared Documents") || drives.value[0];
  return { siteId: site.id, driveId: drive.id };
}


// Standard 22 section subfolders inside Turnover/
// Matches the live TurnoverSections structure exactly (see sp_CreateTurnover's
// seed INSERT) — 18 sections, RACI at 6 / Estimate & Budget at 7 / Vendor &
// Subcontractor Quotes at 8 (session-16 reorder), single merged Correspondence
// at 14 (session-14 merge of former Scope Clarifications + Client Commitments,
// with everything after it renumbered down by one). The previous 19-entry
// version here predated both changes and was never updated, which silently
// broke folder provisioning for every turnover created since — fixed 2026-07-09.
// Slashes in section names are pre-converted to " - " (SharePoint disallows "/"
// in folder names; createFolderByPath's sanitizer would do this anyway, but
// spelling it out here keeps this array showing the actual folder name).
// The list itself lives in config/turnoverLayout.js (subfolderNames) — it
// depends on the turnover's section layout (18 sections vs. 17).

async function createFolderByPath(token, driveId, parentItemId, folderName) {
  // Use item ID directly — avoids all path encoding issues
  // Matches the sanitizer in TurnoverDetail.jsx's uploadToSharePoint exactly —
  // previously missing "\\" and ":" here, which SharePoint also disallows.
  const safeName = folderName.replace(/[\/\\:*?"<>|]/g, "-");

  // Check if already exists
  const listRes = await fetch(
    `${GRAPH_BASE}/drives/${driveId}/items/${parentItemId}/children?$filter=name eq '${encodeURIComponent(safeName)}'`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (listRes.ok) {
    const list = await listRes.json();
    if (list.value?.length > 0) return; // already exists
  }

  // POST using item ID — no path encoding issues
  const postRes = await fetch(
    `${GRAPH_BASE}/drives/${driveId}/items/${parentItemId}/children`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({
        name: safeName,
        folder: {},
      }),
    }
  );

  if (!postRes.ok && postRes.status !== 409) {
    const errText = await postRes.text().catch(() => String(postRes.status));
    throw new Error(`Failed to create folder: ${safeName} (${postRes.status}): ${errText}`);
  }
}

async function provisionProjectFolder(token, driveId, year, folderName, basePath, layout = 1, jobNumber = "") {
  const TURNOVER_SUBFOLDERS = subfolderNames(layout);
  // A folder already filed under this job number — whatever it is named (an
  // older one carries the full deal name) — is used as is, never duplicated.
  const existingForJob = jobNumber ? jobFolderForNumber(await listJobFolders(token, driveId, basePath, year), jobNumber) : null;
  if (existingForJob) folderName = existingForJob.name;
  const newFolderPath     = `${basePath}/${year} Jobs/${folderName}`;
  const turnoverFolderPath = `${newFolderPath}/Turnover`;

  // ── Pre-check: skip copy if folder already exists ────────────────────────
  // Handles re-runs (e.g. test records or retry after partial failure).
  // We still proceed to create any missing Turnover section subfolders.
  let alreadyExisted = false;
  const existCheck = await fetch(
    `${GRAPH_BASE}/drives/${driveId}/root:/${newFolderPath}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (existCheck.ok) {
    alreadyExisted = true;
  }

  if (!alreadyExisted) {
    // Step 1: Find template
    const templatePath = `${basePath}/${year} Jobs/${TEMPLATE_NAME}`;
    const tmplRes = await fetch(
      `${GRAPH_BASE}/drives/${driveId}/root:/${templatePath}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!tmplRes.ok) throw new Error(`Template folder not found at: ${templatePath} (${tmplRes.status})`);
    const tmpl = await tmplRes.json();

    // Step 2: Find destination parent ({year} Jobs)
    const destParentPath = `${basePath}/${year} Jobs`;
    const destParentRes  = await fetch(
      `${GRAPH_BASE}/drives/${driveId}/root:/${destParentPath}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!destParentRes.ok) throw new Error(`Year Jobs folder not found: ${destParentPath}`);
    const destParent = await destParentRes.json();

    // Step 3: Copy template (includes Turnover/ subfolder from template)
    const copyRes = await fetch(
      `${GRAPH_BASE}/drives/${driveId}/items/${tmpl.id}/copy`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          parentReference: { driveId, id: destParent.id },
          name: folderName,
        }),
      }
    );
    if (copyRes.status !== 202 && copyRes.status !== 200) {
      const errText = await copyRes.text();
      throw new Error(`Copy failed (${copyRes.status}): ${errText}`);
    }

    // Step 4: Poll until copy completes (max 60s)
    const monitorUrl = copyRes.headers.get("Location");
    if (monitorUrl) {
      for (let i = 0; i < 20; i++) {
        await new Promise(r => setTimeout(r, 3000));
        const poll   = await fetch(monitorUrl);
        const status = await poll.json();
        if (status.status === "completed") break;
        if (status.status === "failed") throw new Error("SharePoint copy operation failed");
      }
    }

    // Extra settle time — SharePoint copy reports "completed" before folders are fully queryable
    await new Promise(r => setTimeout(r, 8000));

    // Verify copy succeeded by checking for at least one template subfolder (Billing)
    const billingCheck = `${newFolderPath}/Billing`;
    let copyVerified = false;
    for (let v = 0; v < 6; v++) {
      const vRes = await fetch(`${GRAPH_BASE}/drives/${driveId}/root:/${billingCheck}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (vRes.ok) { copyVerified = true; break; }
      await new Promise(r => setTimeout(r, 5000));
    }
    if (!copyVerified) {
      throw new Error("Template copy did not complete — the Billing folder was not found after 40 seconds. Please try again.");
    }
  }

  // Get the project folder (needed in both new and already-existed paths)
  const newRes = await fetch(
    `${GRAPH_BASE}/drives/${driveId}/root:/${newFolderPath}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!newRes.ok) throw new Error(`Could not retrieve folder: ${newFolderPath}`);
  const newFolder = await newRes.json();
  // newFolder.id is a stable Graph drive-item ID — unlike the path string above,
  // it stays valid even if this folder is later renamed or moved within the
  // same drive. Callers should persist it (Turnovers.ConstructionFolderItemId)
  // so future uploads can anchor to the item instead of rebuilding this path
  // from JobNumber + ProjectName, which breaks silently on an out-of-band
  // SharePoint rename. Added 2026-07-09.

  // Get the Turnover/ subfolder — retry up to 5x as SP may need time to settle
  let turnoverFolder = null;
  for (let attempt = 0; attempt < 5; attempt++) {
    const turnoverRes = await fetch(
      `${GRAPH_BASE}/drives/${driveId}/root:/${turnoverFolderPath}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (turnoverRes.ok) {
      turnoverFolder = await turnoverRes.json();
      break;
    }
    await new Promise(r => setTimeout(r, 2000));
  }
  if (!turnoverFolder) throw new Error(`Turnover subfolder not found — SharePoint may still be processing. Wait 30 seconds and try again.`);

  // Create all section subfolders inside Turnover/ — createFolderByPath skips
  // any that already exist, so this is safe to run on an existing folder too.
  // Each folder is isolated in its own try/catch: previously a single failure
  // (transient Graph error, throttling, etc.) threw out of the loop entirely,
  // silently leaving every subfolder after the failed one un-created with no
  // record of what happened — root cause of turnovers missing most of their
  // section folders. Now one bad folder is logged and skipped, not fatal to
  // the rest. Fixed 2026-07-09.
  const failedFolders = [];
  for (const subfolder of TURNOVER_SUBFOLDERS) {
    try {
      await createFolderByPath(token, driveId, turnoverFolder.id, subfolder);
    } catch (e) {
      console.error(`Failed to create section folder "${subfolder}" for ${folderName}:`, e);
      failedFolders.push(subfolder);
    }
  }
  if (failedFolders.length > 0) {
    console.warn(`${folderName}: ${failedFolders.length} of ${TURNOVER_SUBFOLDERS.length} section folders failed to create:`, failedFolders);
  }

  return { webUrl: newFolder.webUrl, itemId: newFolder.id };
}


function AssignJobNumberModal({ turnover, onClose, onAssigned }) {
  const { instance, accounts } = useMsal();
  const [jobNumber, setJobNumber]         = useState("");
  const [spUrl, setSpUrl]                 = useState("");
  const [spItemId, setSpItemId]           = useState("");
  const [suggested, setSuggested]         = useState("");
  const [saving, setSaving]               = useState(false);
  const [provisioning, setProvisioning]   = useState(false);
  const [provisionStatus, setProvisionStatus] = useState(null);
  const [provisionMsg, setProvisionMsg]   = useState("");
  const [contractingEntity, setContractingEntity] = useState("CleanSpace");
  const [subEntities, setSubEntities]     = useState([]); // entities also performing work under this job number

  const ENTITY_OPTIONS = ["CleanSpace", "Allied", "CleanShield", "TECHspace"];
  // Entities that can be subcontracted under a prime contract — excludes whichever is prime.
  const SUB_ENTITY_OPTIONS = ["CleanSpace", "Allied", "CleanShield", "TECHspace", "Manufacturing"];
  // Manufacturing (CleanSpace Systems) never holds a prime contract and has no ACC account.
  const NO_ACC_ENTITIES = ["Manufacturing"];

  async function fetchSuggested(entity) {
    try {
      const rows = await sqlRead(`EXEC sp_GetNextJobNumber @contracting_entity = '${(entity || "").replace(/'/g, "''")}'`);
      if (rows[0]?.SuggestedJobNumber) {
        setSuggested(rows[0].SuggestedJobNumber);
        setJobNumber(rows[0].SuggestedJobNumber);
      }
    } catch {}
  }

  useEffect(() => {
    fetchSuggested(contractingEntity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleEntityChange(entity) {
    setContractingEntity(entity);
    setSubEntities(s => s.filter(e => e !== entity)); // can't sub to yourself
    fetchSuggested(entity);
  }

  function toggleSubEntity(entity) {
    setSubEntities(s => s.includes(entity) ? s.filter(e => e !== entity) : [...s, entity]);
  }

  function getYear() {
    const num = jobNumber.trim();
    return num.includes("-") ? num.split("-")[0] : String(new Date().getFullYear());
  }
  // "<job #> - <deal name without its location>" (folderPath.js jobFolderName).
  function getFolderName() {
    return jobFolderName(jobNumber, turnover.ProjectName);
  }

  async function handleProvisionFolders() {
    if (!jobNumber.trim()) { alert("Enter a job number first."); return; }
    if (!getFolderName())  { alert("Project name is required to create the folder."); return; }
    setProvisioning(true);
    setProvisionStatus(null);
    setProvisionMsg("");
    try {
      const tokenRes = await instance.acquireTokenSilent({
        scopes: ["https://graph.microsoft.com/Sites.ReadWrite.All"],
        account: accounts[0],
      });
      const token       = tokenRes.accessToken;
      const { driveId } = await getConstructionDriveInfo(token);
      const year        = getYear();
      const folderName  = getFolderName();

      // Always create the prime entity's folder first — its webUrl is saved
      // to the turnover record in SQL as the canonical SharePoint folder URL.
      const primeBase = ENTITY_BASE_PATH[contractingEntity] || "CleanSpace";
      setProvisionMsg(`Creating folder for ${contractingEntity}…`);
      const primeResult = await provisionProjectFolder(token, driveId, year, folderName, primeBase, layoutOf(turnover), jobNumber.trim());
      setSpUrl(primeResult.webUrl);
      setSpItemId(primeResult.itemId);

      // Create a matching folder in each subcontracted entity's path.
      // Best-effort — a failure on a sub-entity folder is surfaced but
      // does NOT roll back the prime or block Assign & Activate.
      const subFailed = [];
      for (const sub of subEntities) {
        const subBase = ENTITY_BASE_PATH[sub];
        if (!subBase || subBase === primeBase) continue; // TECHspace falls back to CleanSpace — skip duplicate
        try {
          setProvisionMsg(`Creating folder for ${sub}…`);
          await provisionProjectFolder(token, driveId, year, folderName, subBase, layoutOf(turnover), jobNumber.trim());
        } catch (subErr) {
          console.warn(`Folder creation failed for ${sub}:`, subErr.message);
          subFailed.push(sub);
        }
      }

      setProvisionStatus("success");
      if (subFailed.length > 0) {
        setProvisionMsg(`Folder created for ${contractingEntity}. ⚠️ Failed for: ${subFailed.join(", ")} — create those manually in SharePoint.`);
      } else {
        const allEntities = [contractingEntity, ...subEntities].join(", ");
        setProvisionMsg(`Folders created: ${folderName} (${allEntities})`);
      }
    } catch (e) {
      setProvisionStatus("error");
      setProvisionMsg(e.message);
    } finally {
      setProvisioning(false);
    }
  }

  async function sendJobNumberAssignedEmail(assignedJobNumber) {
    try {
      const [detailRows, contacts] = await Promise.all([
        sqlRead(`EXEC sp_GetTurnoverDetail @turnover_id=${parseInt(turnover.TurnoverID, 10)}`),
        sqlRead("EXEC sp_GetTurnoverConfigContacts"),
      ]);
      let full = {};
      try { full = JSON.parse(detailRows?.[0]?.TurnoverJson || "{}") || {}; } catch { /* list row is enough */ }
      const t = { ...turnover, ...full, TurnoverID: turnover.TurnoverID };
      const token = await getMailToken(instance, accounts, loginScopes);
      // Layout 3: the PM (stored by name on the Cover Sheet) and the Marketing
      // Director join this email to coordinate §18 Marketing.
      let pm = null, director = null;
      if (sectionNumberOf(t, "marketing")) {
        director = marketingDirector(contacts);
        const pmName = String(t.ProjectManager || "").trim();
        if (pmName) {
          try {
            const filter = encodeURIComponent(`displayName eq '${pmName.replace(/'/g, "''")}'`);
            const r = await fetch(`https://graph.microsoft.com/v1.0/users?$filter=${filter}&$select=displayName,mail`, {
              headers: { Authorization: `Bearer ${token.accessToken}` },
            });
            const u = r.ok ? (await r.json())?.value?.find(x => x.mail) : null;
            pm = u ? { name: u.displayName, email: u.mail } : { name: pmName, email: "" };
          } catch { pm = { name: pmName, email: "" }; }
        }
      }
      const { to, cc } = jobNumberAssignedRecipients(contacts, t, { pm: pm?.email ? pm : null });
      if (to.length === 0) return;
      const { subject, html } = buildJobNumberAssignedEmail({
        turnover: t, jobNumber: assignedJobNumber, assignedBy: accounts[0]?.name || "", pm, director,
      });
      const res = await fetch("https://graph.microsoft.com/v1.0/me/sendMail", {
        method: "POST",
        headers: { Authorization: `Bearer ${token.accessToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          message: {
            subject,
            body: { contentType: "HTML", content: html },
            toRecipients: to.map(r => ({ emailAddress: { address: r.email, name: r.name } })),
            ccRecipients: cc.map(r => ({ emailAddress: { address: r.email, name: r.name } })),
          },
          saveToSentItems: true,
        }),
      });
      if (!res.ok) throw new Error(`Graph sendMail ${res.status}`);
    } catch (e) {
      console.error("[Job Number Assigned] notification failed:", e);
      alert("Job number assigned, but the notification email failed to send: " + e.message);
    }
  }

  async function handleAssign() {
    if (!jobNumber.trim()) return;
    setSaving(true);
    try {
      // Step 1: Assign job number and activate
      const payload = JSON.stringify({
        turnover_id:                  turnover.TurnoverID,
        job_number:                   jobNumber.trim(),
        sharepoint_folder_url:        spUrl.trim() || null,
        construction_folder_item_id:  spItemId || null,
        estimating_folder_url:        turnover.EstimatingSpFolderUrl || null,
        contracting_entity:           contractingEntity,
        subcontracted_entities:       subEntities,
      }).replace(/'/g, "''");
      await sqlWrite(`EXEC sp_AssignJobNumber @payload = '${payload}'`);

      // Step 1b: tell the Sales / BD rep (update the HubSpot deal's job number),
      // Marketing (project sheet + tracking) and
      // Sales / Estimating (sections 4–18 unlocked). Never blocks assignment.
      await sendJobNumberAssignedEmail(jobNumber.trim());

      // Step 2: copy everything uploaded before the job number (it sits in the
      // Estimating site) into the job folder — moveDocsToJobFolder.js. Runs
      // whether or not the folder field was filled; never blocks assignment.
      try {
        const detailRows = await sqlRead(`EXEC sp_GetTurnoverDetail @turnover_id=${parseInt(turnover.TurnoverID, 10)}`);
        const row = detailRows?.[0] || {};
        const parse = (v) => { try { return JSON.parse(v || "[]") || []; } catch { return []; } };
        const documents = parse(row.DocumentsJson);
        if (docsInEstimatingSite(documents).length) {
          setProvisionMsg("Copying the contract review documents into the job folder…");
          const tokenRes = await instance.acquireTokenSilent({ scopes: ["https://graph.microsoft.com/Sites.ReadWrite.All"], account: accounts[0] });
          const { driveId: constDriveId } = await getConstructionDriveInfo(tokenRes.accessToken);
          const result = await copyDocsToJobFolder({
            token: tokenRes.accessToken, constDriveId, documents, sections: parse(row.SectionsJson),
            turnover: { ...turnover, JobNumber: jobNumber.trim(), ContractingEntity: contractingEntity, ConstructionFolderItemId: spItemId || null },
            onProgress: setProvisionMsg,
          });
          setProvisionMsg((result.failed.length ? "⚠️ " : "✅ ") + copySummary(result));
          if (result.failed.length) alert("Job number assigned. " + copySummary(result) + " Use “Copy into the job folder” on the turnover's folder strip to retry.");
        }
      } catch (migErr) {
        console.warn("Document copy failed:", migErr);
        setProvisionMsg("⚠️ Job assigned, but the contract review documents weren't copied: " + migErr.message);
        alert("Job number assigned, but the contract review documents weren't copied into the job folder: " + migErr.message + " They can be copied later from the turnover's folder strip.");
      }

      onAssigned();
    } catch (e) {
      alert("Error assigning job number: " + e.message);
    } finally {
      setSaving(false);
    }
  }

  const inputStyle = {
    width: "100%", padding: "8px 12px", fontSize: 14,
    border: "1px solid var(--cs-border)", borderRadius: 6,
    background: "var(--surface)", color: "var(--text)", boxSizing: "border-box",
  };

  const canProvision = jobNumber.trim() && getFolderName();

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
      padding: 16, boxSizing: "border-box",
    }}>
      <div style={{
        background: "var(--surface)", borderRadius: 12,
        width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto",
        boxShadow: "0 20px 60px rgba(0,0,0,0.25)", padding: 28, boxSizing: "border-box",
      }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Assign Job Number</div>
        <div style={{ fontSize: 13, color: "var(--text2)", marginBottom: 20 }}>
          {turnover.ProjectName} — {turnover.ClientName || ""}
        </div>

        {/* Contracting Entity */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text2)", display: "block", marginBottom: 6 }}>
            Contracting Entity <span style={{ fontWeight: 400, color: "var(--text3)" }}>— who holds the prime contract</span>
          </label>
          <select
            value={contractingEntity}
            onChange={e => handleEntityChange(e.target.value)}
            style={inputStyle}
          >
            {ENTITY_OPTIONS.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
          <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4 }}>
            {contractingEntity === "Allied" && "Job number series: yyyy-1XX"}
            {contractingEntity === "CleanShield" && "Job number series: yyyy-6XX (Project work — Service work is handled entirely in ServiceTrade, outside EstAssist)"}
            {contractingEntity === "CleanSpace" && "Job number series: yyyy-2XX"}
            {contractingEntity === "TECHspace" && "Job number series: yyyy-2XX (shares CleanSpace's series)"}
          </div>
        </div>

        {/* Subcontracted entities */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text2)", display: "block", marginBottom: 6 }}>
            Also Subcontracted To <span style={{ fontWeight: 400, color: "var(--text3)" }}>— optional, select any other entities performing work under this same job number</span>
          </label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {SUB_ENTITY_OPTIONS.filter(e => e !== contractingEntity).map(e => {
              const checked = subEntities.includes(e);
              return (
                <label key={e} style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "5px 10px", fontSize: 12,
                  border: `1px solid ${checked ? "#0ea5e9" : "var(--cs-border)"}`,
                  borderRadius: 6, cursor: "pointer",
                  background: checked ? "#f0f9ff" : "none",
                  color: checked ? "#0369a1" : "var(--text2)",
                }}>
                  <input type="checkbox" checked={checked} onChange={() => toggleSubEntity(e)} style={{ margin: 0 }} />
                  {e}{NO_ACC_ENTITIES.includes(e) && <span style={{ fontSize: 10, color: "var(--text3)" }}> (no ACC)</span>}
                </label>
              );
            })}
          </div>
          {subEntities.length > 0 && (
            <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 6, lineHeight: 1.5 }}>
              Job number <strong>{jobNumber || "…"}</strong> will be replicated into QBO for {subEntities.join(", ")}
              {subEntities.some(e => !NO_ACC_ENTITIES.includes(e)) &&
                ` and into ACC for ${subEntities.filter(e => !NO_ACC_ENTITIES.includes(e)).join(", ")}`}.
              {" "}This replication happens via the QBO/ACC integration once connected — see Item 5.
            </div>
          )}
        </div>

        {/* Job number */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text2)", display: "block", marginBottom: 6 }}>
            Job Number {suggested && <span style={{ fontWeight: 400, color: "var(--text3)" }}>(suggested: {suggested})</span>}
          </label>
          <input
            value={jobNumber}
            onChange={e => setJobNumber(e.target.value)}
            style={{ ...inputStyle, fontSize: 18, fontWeight: 700, letterSpacing: 1 }}
            placeholder="e.g. 2026-224"
          />
        </div>

        {/* Folder provisioning */}
        <div style={{
          marginBottom: 16, padding: "14px 16px",
          background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 8,
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#0369a1", marginBottom: 4 }}>
            📁 Create SharePoint Project Folder
          </div>
          <div style={{ fontSize: 11, color: "#0369a1", marginBottom: 10, lineHeight: 1.5 }}>
            Copies the Project Folder Template to:{" "}
            <strong>{ENTITY_BASE_PATH[contractingEntity] || "CleanSpace"} / {getYear()} Jobs / {getFolderName() || "…"}</strong>
          </div>
          <button
            onClick={handleProvisionFolders}
            disabled={provisioning || !canProvision || provisionStatus === "success"}
            style={{
              padding: "7px 16px", fontSize: 12, fontWeight: 700,
              background: provisionStatus === "success" ? "#059669" : "#0ea5e9",
              color: "#fff", border: "none", borderRadius: 6, cursor: "pointer",
              opacity: (provisioning || !canProvision) ? 0.5 : 1,
            }}
          >
            {provisioning ? "Copying template & creating subfolders… (may take 30-60s)" : provisionStatus === "success" ? "✓ Folders Created" : "Create Folders from Template"}
          </button>
          {provisionMsg && (
            <div style={{
              marginTop: 8, fontSize: 12,
              color: provisionStatus === "success" ? "#059669" : "#dc2626",
            }}>
              {provisionStatus === "success" ? "✅ " : "❌ "}{provisionMsg}
            </div>
          )}
        </div>

        {/* SP URL — auto-filled after provisioning or manual paste */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text2)", display: "block", marginBottom: 6 }}>
            Construction SharePoint Folder URL
            <span style={{ fontWeight: 400, color: "var(--text3)" }}> — auto-filled after folder creation, or paste manually</span>
          </label>
          <input
            value={spUrl}
            onChange={e => setSpUrl(e.target.value)}
            style={{ ...inputStyle, fontSize: 11 }}
            placeholder="https://alliedconstructionmanagemen.sharepoint.com/…"
          />
        </div>

        <div style={{
          padding: "12px 14px", background: "#fffbeb",
          border: "1px solid #f59e0b", borderRadius: 8, marginBottom: 24,
          fontSize: 12, color: "#92400e",
        }}>
          ⚠️ Assigning this job number will move the turnover to <strong>Active</strong> status and notify Sales to begin uploading documents.
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button onClick={onClose} style={{
            padding: "8px 18px", fontSize: 13, fontWeight: 600,
            background: "none", border: "1px solid var(--cs-border)",
            borderRadius: 6, cursor: "pointer", color: "var(--text2)",
          }}>Cancel</button>
          <button
            onClick={handleAssign}
            disabled={saving || !jobNumber.trim()}
            style={{
              padding: "8px 20px", fontSize: 13, fontWeight: 600,
              background: "#059669", color: "#fff",
              border: "none", borderRadius: 6, cursor: "pointer",
              opacity: (saving || !jobNumber.trim()) ? 0.5 : 1,
            }}
          >
            {saving ? "Assigning…" : "Assign & Activate"}
          </button>
        </div>
      </div>
    </div>
  );
}
// ── Main Component ─────────────────────────────────────────────────────────────
export default function Turnovers() {
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const isMobile = useIsMobile();
  const navHeight = isMobile ? 52 : 92;
  const currentUser = accounts[0] ? {
    name:  accounts[0].name,
    email: accounts[0].username,
  } : null;

  const { data: turnoversData, loading, refreshing, refresh: refreshTurnovers } = useStaleData(
    "cache:turnovers",
    () => sqlRead("EXEC sp_GetTurnovers").then(rows => rows.slice(0, 50)),
  );
  const turnovers = turnoversData ?? [];
  const [showNew, setShowNew]           = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignNotice, setAssignNotice] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // ?assign=<TurnoverID> — the Job Number Requested email's link. Opens that
  // turnover's Assign Job Number dialog once the list has loaded, then drops
  // the parameter so a refresh doesn't reopen it. The list only keeps 50 rows,
  // so an older turnover is looked up in the full list.
  const assignParam = parseAssignParam(`?${searchParams.toString()}`);
  useEffect(() => {
    if (!assignParam || loading) return;
    let cancelled = false;
    (async () => {
      let hit = resolveAssignTarget(turnovers, assignParam);
      if (hit.state === "not_found") {
        try { hit = resolveAssignTarget(await sqlRead("EXEC sp_GetTurnovers"), assignParam); } catch { /* keep not_found */ }
      }
      if (cancelled) return;
      if (hit.state === "open") { setAssignTarget(hit.turnover); setAssignNotice(null); }
      else if (hit.state === "already_assigned") setAssignNotice(`${hit.turnover.ProjectName} already has job number ${hit.turnover.JobNumber}.`);
      else if (hit.state === "not_ready") setAssignNotice(`${hit.turnover.ProjectName} has not been submitted for a job number (status: ${hit.turnover.Status}).`);
      else setAssignNotice("That turnover could not be found.");
      const next = new URLSearchParams(searchParams);
      next.delete(ASSIGN_PARAM);
      setSearchParams(next, { replace: true });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignParam, loading]);
  const [deleteTarget, setDeleteTarget]   = useState(null);
  const [deleting, setDeleting]           = useState(false);
  const [search, setSearch]             = useState("");
  const [statusFilter, setStatusFilter] = useState("all");


  async function handleDelete(t) {
    setDeleting(true);
    try {
      await sqlWrite(`EXEC sp_DeleteTurnover @turnover_id = ${t.TurnoverID}`);
      setDeleteTarget(null);
      refreshTurnovers();
    } catch (e) {
      alert("Error deleting turnover: " + e.message);
    } finally {
      setDeleting(false);
    }
  }

  const pending = turnovers.filter(t => t.Status === "Pending Number");

  const filtered = turnovers.filter(t => {
    const matchSearch = !search ||
      t.ProjectName?.toLowerCase().includes(search.toLowerCase()) ||
      t.ClientName?.toLowerCase().includes(search.toLowerCase()) ||
      t.JobNumber?.toLowerCase().includes(search.toLowerCase()) ||
      t.Salesperson?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === "all" || t.Status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", height: `calc(100vh - ${navHeight}px)` }}>

      {/* Accounting Alert Banner */}
      {pending.length > 0 && (
        <div style={{
          background: "#fffbeb", borderBottom: "2px solid #f59e0b",
          padding: isMobile ? "10px 14px" : "10px 24px",
          display: "flex", alignItems: "center", gap: 12,
          flexShrink: 0, flexWrap: isMobile ? "wrap" : "nowrap",
        }}>
          <span style={{ fontSize: 16 }}>🔔</span>
          <div style={{ flex: 1, minWidth: isMobile ? "100%" : "auto" }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#92400e" }}>
              {pending.length} turnover{pending.length > 1 ? "s" : ""} awaiting job number assignment
            </span>
            {!isMobile && (
              <span style={{ fontSize: 12, color: "#b45309", marginLeft: 8 }}>
                {pending.map(t => t.ProjectName).join(" · ")}
              </span>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {pending.map(t => (
              <button
                key={t.TurnoverID}
                onClick={() => setAssignTarget(t)}
                style={{
                  padding: "5px 14px", fontSize: 12, fontWeight: 700,
                  background: "#f59e0b", color: "#fff",
                  border: "none", borderRadius: 6, cursor: "pointer",
                }}
              >
                Assign #{t.ProjectName.substring(0, 20)}{t.ProjectName.length > 20 ? "…" : ""}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{
        padding: isMobile ? "12px 14px 10px" : "14px 24px 12px",
        borderBottom: "1px solid var(--cs-border)",
        background: "var(--surface)", flexShrink: 0,
      }}>
        <div style={{
          display: "flex", alignItems: isMobile ? "flex-start" : "center",
          justifyContent: "space-between", marginBottom: 12,
          flexDirection: isMobile ? "column" : "row", gap: isMobile ? 10 : 0,
        }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text)" }}>Turnover Packages</div>
            <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 1 }}>
              Sales-to-Construction handoff tracker
            </div>
          </div>
          <button
            onClick={() => setShowNew(true)}
            style={{
              padding: "8px 18px", fontSize: 13, fontWeight: 700,
              background: "var(--cs-blue)", color: "#fff",
              border: "none", borderRadius: 8, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 6,
              width: isMobile ? "100%" : "auto", justifyContent: "center",
            }}
          >
            + New Turnover
          </button>
        </div>

        {/* Filters */}
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: isMobile ? "wrap" : "nowrap" }}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search project, client, job #…"
            style={{ flex: 1, maxWidth: isMobile ? "none" : 300, minWidth: isMobile ? "100%" : "auto", fontSize: isMobile ? 16 : 13 }}
          />
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            style={{
              padding: "6px 10px", fontSize: isMobile ? 16 : 12, border: "1px solid var(--cs-border)",
              borderRadius: 6, background: "var(--surface)", color: "var(--text)",
              flex: isMobile ? 1 : "none",
            }}
          >
            <option value="all">All Statuses</option>
            {Object.keys(STATUS_CONFIG).map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <span style={{ fontSize: 12, color: "var(--text3)", marginLeft: isMobile ? 0 : "auto" }}>
            {!loading && `${filtered.length} turnover${filtered.length !== 1 ? "s" : ""}`}
            {refreshing && <span style={{ marginLeft: 8, opacity: 0.5 }}>↻</span>}
          </span>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        {loading && (
          <div style={{ padding: 40, textAlign: "center", color: "var(--text3)", fontSize: 13 }}>
            <div className="spinner spinner-dark" style={{ display: "inline-block", marginRight: 8 }} />
            Loading turnovers…
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div style={{ padding: 60, textAlign: "center", color: "var(--text3)" }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>📋</div>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>No turnovers yet</div>
            <div style={{ fontSize: 13 }}>Click "+ New Turnover" to start a package</div>
          </div>
        )}

        {/* ── Mobile: card list ─────────────────────────────────────────────── */}
        {isMobile && !loading && filtered.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: 14 }}>
            {filtered.map(t => {
              const fi = parseLineItems(t.LineItemsJson);
              return (
                <div
                  key={t.TurnoverID}
                  onClick={() => navigate(`/turnover/${t.TurnoverID}`)}
                  style={{
                    background: "#fff", border: "1px solid var(--cs-border)", borderRadius: 10,
                    padding: 14, cursor: "pointer",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: "var(--text)" }}>{t.ProjectName}</div>
                      {t.ClientName && <div style={{ fontSize: 12, color: "var(--text2)", marginTop: 1 }}>{t.ClientName}</div>}
                    </div>
                    <button
                      onClick={e => { e.stopPropagation(); setDeleteTarget(t); }}
                      style={{
                        background: "none", border: "none", cursor: "pointer",
                        fontSize: 16, color: "#d1d5db", padding: "2px 4px", flexShrink: 0,
                      }}
                      title="Delete turnover"
                    >
                      🗑
                    </button>
                  </div>

                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
                    <StatusPill status={t.Status} />
                    {t.JobNumber
                      ? <span style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 12, color: "var(--cs-blue)" }}>{t.JobNumber}</span>
                      : <span style={{ fontSize: 11, color: "#f59e0b", fontStyle: "italic" }}>Pending #</span>}
                    {t.OpenActionItems > 0 && (
                      <span style={{ fontSize: 10, color: "#dc2626", fontWeight: 600 }}>
                        {t.OpenActionItems} open action{t.OpenActionItems > 1 ? "s" : ""}
                      </span>
                    )}
                  </div>

                  <div style={{ marginBottom: 8 }}>
                    <ProgressBar
                      pct={t.CompletionPct || 0}
                      complete={t.SectionsComplete || 0}
                      total={t.SectionsApplicable || 25}
                    />
                  </div>

                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--text3)" }}>
                    <span>{t.Salesperson || "—"}</span>
                    <span>{fmtDate(t.PlannedTurnoverDate)}</span>
                    <span style={{ fontWeight: 600, color: "var(--text2)" }}>
                      {fi && fi.count > 0 ? fmtM(fi.total) : fmtCurrency(t.DealAmount)}
                    </span>
                  </div>

                  {t.Status === "Pending Number" && (
                    <button
                      onClick={e => { e.stopPropagation(); setAssignTarget(t); }}
                      style={{
                        marginTop: 10, width: "100%", padding: "8px", fontSize: 12, fontWeight: 700,
                        background: "#f59e0b", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer",
                      }}
                    >
                      Assign Job Number
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── Desktop: table ────────────────────────────────────────────────── */}
        {!isMobile && !loading && filtered.length > 0 && (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead style={{ background: "#FAFBFC", position: "sticky", top: 0, zIndex: 1 }}>
              <tr>
                {["Job #", "Project", "Client", "Salesperson", "Financials", "Turnover Date", "Progress", "Status", ""].map(h => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => (
                <tr
                  key={t.TurnoverID}
                  style={{ borderBottom: "1px solid var(--cs-border)", cursor: "pointer", transition: "background 0.1s" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#f8f9fb"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  onClick={() => navigate(`/turnover/${t.TurnoverID}`)}
                >
                  <td style={tdStyle}>
                    {t.JobNumber
                      ? <span style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 13, color: "var(--cs-blue)" }}>{t.JobNumber}</span>
                      : <span style={{ fontSize: 11, color: "#f59e0b", fontStyle: "italic" }}>Pending</span>}
                  </td>
                  <td style={{ ...tdStyle, minWidth: 180 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{t.ProjectName}</div>
                    {t.Market && <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 1 }}>{t.Market}</div>}
                  </td>
                  <td style={{ ...tdStyle, fontSize: 12, color: "var(--text2)", minWidth: 140 }}>{t.ClientName || "—"}</td>
                  <td style={{ ...tdStyle, fontSize: 12, color: "var(--text2)", minWidth: 130 }}>{t.Salesperson || "—"}</td>
                  <td style={{ ...tdStyle }}>
                    {(() => {
                      const fi = parseLineItems(t.LineItemsJson);
                      if (fi && fi.count > 0) return (
                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                            <span style={{ fontSize: 10, fontWeight: 700, color: "#059669", minWidth: 28 }}>REL</span>
                            <span style={{ fontSize: 12, fontWeight: 600, color: "#059669" }}>{fmtM(fi.released)}</span>
                          </div>
                          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                            <span style={{ fontSize: 10, fontWeight: 700, color: "#f59e0b", minWidth: 36 }}>FCST</span>
                            <span style={{ fontSize: 12, color: "#b45309" }}>{fmtM(fi.forecasted)}</span>
                          </div>
                          <div style={{ display: "flex", gap: 6, alignItems: "center", borderTop: "1px solid var(--cs-border)", paddingTop: 2, marginTop: 1 }}>
                            <span style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", minWidth: 28 }}>TOT</span>
                            <span style={{ fontSize: 11, color: "var(--text2)" }}>{fmtM(fi.total)}</span>
                          </div>
                        </div>
                      );
                      return <span style={{ fontSize: 12, color: "var(--text2)" }}>{fmtCurrency(t.DealAmount)}</span>;
                    })()}
                  </td>
                  <td style={{ ...tdStyle, fontSize: 12, color: "var(--text2)" }}>{fmtDate(t.PlannedTurnoverDate)}</td>
                  <td style={{ ...tdStyle, minWidth: 140 }}>
                    <ProgressBar
                      pct={t.CompletionPct || 0}
                      complete={t.SectionsComplete || 0}
                      total={t.SectionsApplicable || 25}
                    />
                  </td>
                  <td style={tdStyle}>
                    <StatusPill status={t.Status} />
                    {t.OpenActionItems > 0 && (
                      <div style={{ fontSize: 10, color: "#dc2626", marginTop: 3, fontWeight: 600 }}>
                        {t.OpenActionItems} open action{t.OpenActionItems > 1 ? "s" : ""}
                      </div>
                    )}
                  </td>
                  <td style={{ ...tdStyle, width: 100 }} onClick={e => {
                    e.stopPropagation();
                    if (t.Status === "Pending Number") setAssignTarget(t);
                  }}>
                    {t.Status === "Pending Number" && (
                      <button style={{
                        padding: "4px 10px", fontSize: 11, fontWeight: 700,
                        background: "#f59e0b", color: "#fff",
                        border: "none", borderRadius: 5, cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}>
                        Assign #
                      </button>
                    )}
                  </td>
                  <td style={{ ...tdStyle, width: 36, textAlign: "center" }} onClick={e => { e.stopPropagation(); setDeleteTarget(t); }}>
                    <button style={{
                      background: "none", border: "none", cursor: "pointer",
                      fontSize: 15, color: "#d1d5db", padding: "2px 4px", borderRadius: 4,
                      transition: "color 0.15s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.color = "#dc2626"}
                    onMouseLeave={e => e.currentTarget.style.color = "#d1d5db"}
                    title="Delete turnover"
                    >
                      🗑
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modals */}
      {showNew && (
        <NewTurnoverModal
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); refreshTurnovers(); }}
          currentUser={currentUser}
        />
      )}
      {assignNotice && (
        <div style={{ position: "fixed", top: navHeight + 12, left: "50%", transform: "translateX(-50%)", zIndex: 900, background: "#fffbeb", border: "1px solid #f59e0b", color: "#92400e", borderRadius: 8, padding: "10px 16px", fontSize: 13, boxShadow: "0 6px 20px rgba(0,0,0,0.12)", display: "flex", gap: 12, alignItems: "center" }}>
          <span>{assignNotice}</span>
          <button onClick={() => setAssignNotice(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "#92400e", fontSize: 14 }}>✕</button>
        </div>
      )}
      {assignTarget && (
        <AssignJobNumberModal
          turnover={assignTarget}
          onClose={() => setAssignTarget(null)}
          onAssigned={() => { setAssignTarget(null); refreshTurnovers(); }}
        />
      )}
      {deleteTarget && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
          padding: 16, boxSizing: "border-box",
        }}>
          <div style={{
            background: "var(--surface)", borderRadius: 12, width: "100%", maxWidth: 400,
            padding: 28, boxShadow: "0 20px 60px rgba(0,0,0,0.25)", boxSizing: "border-box",
          }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Delete Turnover?</div>
            <div style={{ fontSize: 13, color: "var(--text2)", marginBottom: 6 }}>
              <strong>{deleteTarget.ProjectName}</strong>
            </div>
            <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 20, lineHeight: 1.5 }}>
              This will permanently delete the turnover record and all associated sections and action items.
              The SharePoint folder will <strong>not</strong> be deleted.
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => setDeleteTarget(null)} style={{
                padding: "8px 18px", fontSize: 13, fontWeight: 600,
                background: "none", border: "1px solid var(--cs-border)",
                borderRadius: 6, cursor: "pointer", color: "var(--text2)",
              }}>Cancel</button>
              <button
                onClick={() => handleDelete(deleteTarget)}
                disabled={deleting}
                style={{
                  padding: "8px 20px", fontSize: 13, fontWeight: 700,
                  background: "#dc2626", color: "#fff",
                  border: "none", borderRadius: 6, cursor: "pointer",
                  opacity: deleting ? 0.5 : 1,
                }}
              >
                {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const thStyle = {
  padding: "8px 10px", fontSize: 11, fontWeight: 600,
  color: "var(--text3)", textTransform: "uppercase",
  letterSpacing: "0.06em", whiteSpace: "nowrap",
  borderBottom: "1px solid var(--cs-border)", textAlign: "left",
};
const tdStyle = {
  padding: "10px 10px", verticalAlign: "middle",
};
